import { findIndex } from 'lodash';
import { fetchAPI } from '../helpers';
import { IFarm, IProduct, IPedido } from '../types';
import appData from './appData';
import { tenant_dev_nealia_origin } from './DummyData';

const envBaseUrl = process.env.REACT_APP_API_BASE_URL as string | undefined;
let baseURL: string = '';

if (envBaseUrl && envBaseUrl.length > 0) {
    baseURL = envBaseUrl.endsWith('/') ? envBaseUrl : `${envBaseUrl}/`;
} else {
    baseURL = tenant_dev_nealia_origin.includes(window.location.hostname)
        ? 'https://fnc-nealia-feed-99.azurewebsites.net/api/'
        : 'https://fnc-nealia-feed-01.azurewebsites.net/api/';
}

class OrderService {
    
    static getFarms = async (refresh:boolean) => {
        try{
            let url = `${baseURL}loadFarms`
            if(appData.getListFarms() == undefined || appData.getListFarms().length == 0 || refresh) {
                let response = await fetchAPI(url, 'GET', true, undefined, false);
                if (response.valid)
                    appData.setListFarms(response.data);
                else
                    appData.setListFarms([]);
            }
            return appData.getListFarms();
        }
        catch(err){
            appData.setListFarms(undefined);
            return [];
        }
    }

    static getFarm = async (business: string, id: string) => {
        const farms = appData.getListFarmDetail();

        const idx = findIndex(farms, function (o) { return o.business == business && o.id == id });
        if (idx == -1) {
            let url = `${baseURL}loadFarmDetail?business=${business}&id=${id}`;
            let response = await fetchAPI(url, 'GET', true, undefined, false);

            if (!response.valid)
                return null;

            const farm = response.data;
            appData.setListFarmDetail(farm);

            return farm;
        } else
            return farms[idx];
    }  

    static getProducts = async (refresh:boolean, farmId?: string) => {
        try{
            let url = `${baseURL}loadProducts${farmId ? `?farmId=${encodeURIComponent(farmId)}` : ''}`
            if(appData.getListProducts() == undefined || refresh) {
                let response = await fetchAPI(url, 'GET', true, undefined, false);

                if (response.valid)
                    appData.setListProducts(response.data);
                else
                    appData.setListProducts([]);
            }
            return appData.getListProducts();
        }
        catch(err){
            appData.setListProducts(undefined);
            return [];
        }
    }

    static getProduct = async (productRef: string, business:string) => {
        if(appData.getListProducts() === undefined){
            await OrderService.getProducts(false)
        }
        let product = appData.getListProducts().find((currProduct:IProduct) => {
            return currProduct.itemId === productRef && currProduct.business === business;
        });
        return product;
    }   
    
    static getPedidos = async (refresh:boolean, type: number, dateFrom?: string, dateTo?: string) => {
        try{
            let url = `${baseURL}loadPedidos?type=${type}`
            if (dateFrom) url += `&dateFrom=${dateFrom}`;
            if (dateTo) url += `&dateTo=${dateTo}`;

            const sortByIdPedidoDesc = (list:any[]) => {
                return [...list].sort((a:any, b:any) => {
                    const getNum = (p:any) => {
                        const raw = (p?.idPedido ?? p?.id ?? '').toString();
                        const match = raw.match(/\d+$/);
                        return match ? parseInt(match[0], 10) : 0;
                    };
                    return getNum(b) - getNum(a);
                });
            };

            // Usar caché por tipo salvo que se pida refresh explícitamente
            if(appData.getListPedidos(type) == undefined || refresh) {
                let response = await fetchAPI(url, 'GET', true, undefined, false);
                if(Array.isArray(response)){
                    appData.setListPedidos(sortByIdPedidoDesc(response), type);
                } else {
                    appData.setListPedidos(response, type);
                }
            } else {
                const current = appData.getListPedidos(type);
                if(Array.isArray(current)){
                    appData.setListPedidos(sortByIdPedidoDesc(current), type);
                }
            }
            return appData.getListPedidos(type);
        }
        catch(err){
            appData.setListPedidos(undefined, type);
            return [];
        }
    }

    static getPedido = async (business: string, id: string, type: number) => {
        if(appData.getListPedidos(type) === undefined){
            await OrderService.getPedidos(false, type)
        }
        let pedido = appData.getListPedidos(type).find((currPedido:IPedido) => {
            const sameBusiness = currPedido.business == business;
            const sameId = (currPedido.id !== undefined && String(currPedido.id) == id) || (currPedido.idPedido !== undefined && currPedido.idPedido == id);
            return sameBusiness && sameId;
        });
        return pedido;
    }

    static getPedidosHistorico = async (dateFrom: Date, dateTo: Date, farmId?: string, batchId?: string) => {
        try{
            const dateFromStr = dateFrom.toISOString().split('T')[0];
            const dateToStr = dateTo.toISOString().split('T')[0];
            let url = `${baseURL}loadHistorico?dateFrom=${dateFromStr}&dateTo=${dateToStr}`
            
            if (farmId) url += `&farmId=${encodeURIComponent(farmId)}`;
            if (batchId) url += `&batchId=${encodeURIComponent(batchId)}`;

            const sortByIdPedidoDesc = (list:any[]) => {
                return [...list].sort((a:any, b:any) => {
                    const getNum = (p:any) => {
                        const raw = (p?.idPedido ?? p?.id ?? '').toString();
                        const match = raw.match(/\d+$/);
                        return match ? parseInt(match[0], 10) : 0;
                    };
                    return getNum(b) - getNum(a);
                });
            };

            let response = await fetchAPI(url, 'GET', true, undefined, false);
            
            // Nueva estructura: { granjas, lotes, pedidos }
            if (response && response.pedidos) {
                return {
                    granjas: response.granjas || [],
                    lotes: response.lotes || [],
                    pedidos: sortByIdPedidoDesc(response.pedidos)
                };
            }
            
            // Fallback para estructura antigua
            if(Array.isArray(response)){
                return { granjas: [], lotes: [], pedidos: sortByIdPedidoDesc(response) };
            }
            
            return { granjas: [], lotes: [], pedidos: [] };
        }
        catch(err){
            return { granjas: [], lotes: [], pedidos: [] };
        }
    }   

    static getPedidosRelated = async (refresh:boolean) => {
        try{
            let url = `${baseURL}loadRelatedPedidos`

            if(appData.getListPedidosRelated() == undefined || refresh || appData.getListPedidosRelated().length == 0) {
                let response = await fetchAPI(url, 'GET', true, undefined, false);
                appData.setListPedidosRelated(response);
            }
            return appData.getListPedidosRelated();
        }
        catch(err){
            appData.setListPedidosRelated(undefined);
            return [];
        }
    }

    static postDelivery = async (pedido:IPedido) => {
        try{
            let url = `${baseURL}saveDelivery`
            let response = await fetchAPI(url, 'POST', true, {...pedido}, false);
            return response;
        }
        catch(err){
            return -1;
        }
    }

    static saveData = async (pedido:IPedido, action: number) => {
        try{
            let url = `${baseURL}saveData?action=${action}`
            let response = await fetchAPI(url, 'POST', true, {...pedido}, false);

            if (!response.valid)
                return null;
            return response.data;
        }
        catch(err){
            return -1;
        }
    }

    static savePedido = async (pedido:IPedido, action: number) => {
        try{
            let url = `${baseURL}savePedido?action=${action}`
            let response = await fetchAPI(url, 'POST', true, {...pedido}, false);

            return response;
        }
        catch(err){
            return -1;
        }
    }

    static me = async () => {
        try{
            let url = `${baseURL}me`
            let response = await fetchAPI(url, 'GET', true, undefined, false);
            //console.log(response)
            return response;
        }
        catch(err){
            return null;
        }
    }
    
    static metricService = async (metricType:number) => { // Llamadas de registro de métricas //
        try {
            let url = `${baseURL}metrics?metricType=${metricType}&origin=${window.location.href}`
            await fetchAPI(url, 'PUT', true, undefined, false);
        } catch(err) {
        }
    }
}
export default OrderService;